/**
 * Синхронизация с сервером (I2): журнал после волны, покупки, офлайн-доход и серия через API.
 *
 * Правила (решения impl-grill 2026-10-24, MADR 0002):
 * - состояние из ответа сервера — истина: монеты клиент начисляет сразу (оптимистично), ответ их заменяет, расхождение
 *   (`corrected`, отказ, ответ по более старой забежке) откатывает игрока к ответу;
 * - журнал уходит не раньше, чем волна длится по тикам (иначе 429 `rate_limited`); отказ 429 и сетевые ошибки — повтор
 *   с задержкой; очередь без сети — максимум один журнал сверх отправляемого, следующие отбрасываются (их заберёт откат);
 * - 409 `balance_version_unsupported` — перезагрузка страницы; прочие отказы журнала — журнал сбрасывается, состояние
 *   запрашивается заново;
 * - покупки и клейм-запросы идут с `Idempotency-Key`: повтор после обрыва сети не спишет дважды;
 * - покупки идут цепочкой; ответ сервера на одну — основа, поверх неё заново применяются ещё не отвеченные (иначе ответ
 *   «возвращал» монеты уже нажатых покупок и кнопка загоралась сверх кошелька сервера, ISSUE-149). Отказ сбрасывает
 *   всю очередь и берёт состояние сервера, без возврата к снимку до покупки, если сервер ответил.
 *
 * Модуль не знает ни DOM, ни стора: всё приходит через {@link SyncDeps}, поэтому тестируется без браузера. Импортирует
 * только контракты и тип транспорта (не `app/index`: цикл через `ui/windows/actions`).
 */
import type {
  AdminToolRequest,
  DailyTaskClaim,
  FriendsResponse,
  LeagueResponse,
  PlayerState,
  PurchaseRequest,
  ShopItem,
  WaveLog,
} from "@newfungame/contracts";
import { ApiError, TICK_MS } from "@newfungame/contracts";
import type { ApiTransport } from "../api/transport";

/** Доля длины волны, раньше которой журнал не шлём (сервер принимает от 0,9; запас на неровный кадр). */
export const SEND_PACE = 0.95;
export const RETRY_BASE_MS = 1000;
export const RETRY_MAX_MS = 30_000;

/** Чем кончилась попытка сдать журнал — по этому игра берёт сид следующей волны или продолжает офлайн. */
export type WaveSyncEvent =
  /** Ответ получен, состояние игрока — сервера. */
  | "acked"
  /** Нет сети (или сервер не отвечает): журнал в очереди, играем на локальном сиде. */
  | "offline"
  /** Журнал не поставлен в очередь (не вошли, очередь занята): играем на локальном сиде. */
  | "dropped"
  /** Сервер отверг журнал, состояние запрошено заново — продолжаем с него. */
  | "rejected";

export interface SyncDeps {
  api: ApiTransport;
  getPlayer(): PlayerState | null;
  /** Подтверждённое состояние (ответ сервера) — в стор. */
  setPlayer(player: PlayerState): void;
  /** Журнал обработан: ответ получен, нет сети или отброшен. */
  onWave?(event: WaveSyncEvent): void;
  /** Версия баланса клиента не поддерживается сервером. */
  reload(): void;
  /** Ошибка операции для игрока (покупка отклонена и т. п.). */
  onError?(code: string): void;
  /** Сервер отказал в покупке «не хватает монет»; `serverCoins` — его кошелёк после отказа. */
  onCoinsShort?(serverCoins: number): void;
  /** Применить покупку локально (engine `applyPurchase`); `null` — не применяется. Без него ответ просто заменяет игрока. */
  applyPurchase?(player: PlayerState, req: PurchaseRequest): PlayerState | null;
  /** Доля длины волны до отправки журнала (по умолчанию {@link SEND_PACE}); 0 — без ожидания (мок без сервера). */
  pace?: number;
  /** Множитель доли: у VIP сервер принимает волну вдвое быстрее (x2, MADR 0003). */
  paceScale?(): number;
  now?(): number;
  setTimer?(fn: () => void, ms: number): unknown;
  clearTimer?(id: unknown): void;
  /** Ключ идемпотентности. */
  newKey?(): string;
}

export interface Sync {
  /** Началась волна: от этого момента считается длина волны. */
  waveStarted(): void;
  /** Журнал завершённой волны — в очередь. */
  submitWave(log: WaveLog): void;
  /** Покупки по порядку: монеты и уровни меняются сразу, ответ сервера решает. */
  purchase(requests: PurchaseRequest[]): Promise<void>;
  claimOffline(): Promise<void>;
  claimStreak(): Promise<void>;
  /** Забрать награду задания; вернёт начисленные монеты (0 — отказ или уже забрано). */
  claimTask(task: DailyTaskClaim): Promise<number>;
  claimSeason(): Promise<void>;
  league(): Promise<LeagueResponse | null>;
  friends(): Promise<FriendsResponse | null>;
  claimLeague(): Promise<void>;
  spend(item: ShopItem): Promise<void>;
  /** Инструмент ускорения админа. */
  adminTool(action: AdminToolRequest["action"]): Promise<void>;
  /** «Забрать всё» в окне «С возвращением». */
  claimWelcomeBack(claim: { offline: boolean; streak: boolean }): Promise<void>;
  /** Сеть вернулась: повторить отправку сейчас. */
  online(): void;
  /** Запросить состояние заново (откат). */
  refresh(): Promise<void>;
  /** Журналов в очереди (отправляемый и ждущий). */
  pending(): number;
  dispose(): void;
}

const isRetryable = (e: unknown): boolean =>
  !(e instanceof ApiError) || e.code === "rate_limited" || e.code === "internal";

export function createSync(deps: SyncDeps): Sync {
  const now = deps.now ?? Date.now;
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  const newKey = deps.newKey ?? (() => crypto.randomUUID().replaceAll("-", ""));

  /** Отправляемый журнал и, возможно, один ждущий за ним. */
  const queue: WaveLog[] = [];
  let sending = false;
  let attempt = 0;
  let timer: unknown = null;
  let waveStartAt = now();
  let lastAckAt = 0;
  let disposed = false;
  let purchaseChain: Promise<void> = Promise.resolve();
  /** Нажатые, но ещё не отвеченные покупки — по порядку. */
  let pendingBuys: PurchaseRequest[] = [];
  /** Растёт на каждом отказе: покупки, вставшие в очередь до него, не отправляются. */
  let buyEpoch = 0;

  const emit = (e: WaveSyncEvent): void => {
    // всегда асинхронно: вызывающий (контроллер) к этому моменту закончил расчёт волны
    queueMicrotask(() => {
      if (!disposed) deps.onWave?.(e);
    });
  };

  const fetchState = async (): Promise<PlayerState | null> => {
    try {
      const state = await deps.api.call("playerState", {});
      deps.setPlayer(state);
      return state;
    } catch {
      // нет сети: остаёмся на локальном, следующий ответ сервера выровняет
      return null;
    }
  };

  /** Ответ сервера + покупки, на которые ответа ещё нет (их уже видно на экране). */
  const withPending = (state: PlayerState): PlayerState => {
    if (!deps.applyPurchase) return state;
    let p = state;
    for (const req of pendingBuys) p = deps.applyPurchase(p, req) ?? p;
    return p;
  };

  const schedule = (ms: number): void => {
    if (timer !== null) clearTimer(timer);
    timer = setTimer(() => {
      timer = null;
      void pump();
    }, ms);
  };

  /** Отправить журнал из головы очереди. */
  const pump = async (): Promise<void> => {
    if (sending || disposed || queue.length === 0) return;
    const log = queue[0]!;
    // не раньше длины волны: от её начала или от прошлого ответа сервера (с него сервер меряет темп)
    const earliest =
      Math.max(waveStartAtOf.get(log) ?? 0, lastAckAt) +
      log.result.ticks * TICK_MS * (deps.pace ?? SEND_PACE) * (deps.paceScale?.() ?? 1);
    const wait = earliest - now();
    if (wait > 0 && attempt === 0) {
      schedule(wait);
      return;
    }
    sending = true;
    try {
      const res = await deps.api.call("submitWave", log);
      queue.shift();
      attempt = 0;
      lastAckAt = now();
      // за этим журналом ждёт следующий — не откатываем игрока на состояние до него
      if (queue.length === 0) deps.setPlayer(res.state);
      emit("acked");
    } catch (e) {
      if (e instanceof ApiError && e.code === "balance_version_unsupported") {
        deps.reload();
        sending = false;
        return;
      }
      if (isRetryable(e)) {
        attempt++;
        if (attempt === 1) emit("offline");
        schedule(Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.min(attempt - 1, 5)));
        sending = false;
        return;
      }
      // отказ по существу: журнал не примут никогда — выбросить и выровняться по серверу
      queue.length = 0;
      attempt = 0;
      sending = false;
      await fetchState();
      emit("rejected");
      return;
    }
    sending = false;
    void pump();
  };

  const waveStartAtOf = new WeakMap<WaveLog, number>();

  /** Подождать, пока журналы уйдут (не дольше `ms`): ответ на покупку не должен откатить неотправленную волну. */
  const drained = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      const until = now() + ms;
      const check = (): void => {
        if (queue.length === 0 || disposed || now() >= until) resolve();
        else setTimer(check, 100);
      };
      check();
    });

  /** Операция API: ошибка не бросается, а уходит в `onError`; `null` — не вышло. */
  const run = async <T>(op: () => Promise<T>): Promise<T | null> => {
    try {
      return await op();
    } catch (e) {
      deps.onError?.(e instanceof ApiError ? e.code : "network");
      return null;
    }
  };

  return {
    waveStarted() {
      waveStartAt = now();
    },
    submitWave(log) {
      if (!deps.getPlayer()) {
        // не вошли: журнал некому принять, бой идёт на начальной забежке
        emit("dropped");
        return;
      }
      if (queue.length >= 2) {
        // очередь без сети — максимум 1 ждущий журнал: остальные откатит ответ сервера
        emit("dropped");
        return;
      }
      waveStartAtOf.set(log, waveStartAt);
      queue.push(log);
      void pump();
    },
    purchase(requests) {
      // до оптимистичной покупки в игре: к этому состоянию откат, если нет ни ответа, ни сети
      const before = deps.getPlayer();
      pendingBuys = [...pendingBuys, ...requests];
      const epoch = buyEpoch;
      // покупки идут строго по очереди: ответ на одну не должен перетереть другую
      const job = purchaseChain.then(() => doPurchase(requests, before, epoch));
      purchaseChain = job.catch(() => {});
      return job;
    },
    async claimOffline() {
      const res = await run(() => deps.api.call("claimOffline", {}, { idempotencyKey: newKey() }));
      if (res) deps.setPlayer(res.state);
    },
    async claimStreak() {
      const res = await run(() => deps.api.call("claimStreak", {}, { idempotencyKey: newKey() }));
      if (res) deps.setPlayer(res.state);
    },
    async claimTask(task) {
      const res = await run(() => deps.api.call("claimTask", { task }, { idempotencyKey: newKey() }));
      if (res) deps.setPlayer(res.state);
      else await fetchState();
      return res?.coinsAwarded ?? 0;
    },
    async claimSeason() {
      const res = await run(() => deps.api.call("claimSeason", {}, { idempotencyKey: newKey() }));
      if (res) deps.setPlayer(res.state);
      else await fetchState();
    },
    league: () => run(() => deps.api.call("league", {})),
    friends: () => run(() => deps.api.call("friends", {})),
    async claimLeague() {
      const res = await run(() => deps.api.call("claimLeague", {}, { idempotencyKey: newKey() }));
      if (res) deps.setPlayer(res.state);
      else await fetchState();
    },
    async spend(item) {
      const res = await run(() => deps.api.call("spend", { item }, { idempotencyKey: newKey() }));
      if (res) deps.setPlayer(res.state);
      else await fetchState();
    },
    async adminTool(action) {
      const res = await run(() => deps.api.call("adminTool", { action }));
      // за журналом в очереди придёт ответ новее: он уже с этими монетами
      if (res && queue.length === 0) deps.setPlayer(res);
    },
    async claimWelcomeBack({ offline, streak }) {
      if (offline) await this.claimOffline();
      if (streak) await this.claimStreak();
    },
    online() {
      attempt = 0;
      if (timer !== null) clearTimer(timer);
      timer = null;
      void pump();
    },
    refresh: async () => {
      await fetchState();
    },
    pending: () => queue.length,
    dispose() {
      disposed = true;
      if (timer !== null) clearTimer(timer);
    },
  };

  async function doPurchase(requests: PurchaseRequest[], before: PlayerState | null, epoch: number): Promise<void> {
    // очередь сброшена отказом раньше: эти покупки уже убраны с экрана
    if (epoch !== buyEpoch) return;
    await drained(3000);
    for (const req of requests) {
      const failed = { code: "" };
      const res = await run(async () => {
        try {
          return await deps.api.call("purchase", req, { idempotencyKey: newKey() });
        } catch (e) {
          failed.code = e instanceof ApiError ? e.code : "network";
          throw e;
        }
      });
      if (epoch !== buyEpoch) return;
      if (res) {
        pendingBuys = pendingBuys.slice(1);
        deps.setPlayer(withPending(res.state));
        continue;
      }
      // отказ или нет сети: остальные нажатые покупки не шлём — состояние берём у сервера
      buyEpoch++;
      pendingBuys = [];
      const state = await fetchState();
      // сервер не ответил и на запрос состояния — вернуть игрока к тому, что было до этих покупок
      if (!state && before) deps.setPlayer(before);
      if (state && failed.code === "insufficient_coins") deps.onCoinsShort?.(state.coins);
      return;
    }
  }
}
